import { ADD_ON_KIND } from "components/admin/FilterBar/AddOnOptions"
import { ASSIGNMENT_KIND } from "components/admin/FilterBar/AssignmentOptions"
import { ATTENDANCE_KIND } from "components/admin/FilterBar/AttendanceOptions"
import { ATTENDEE_TYPE_KIND } from "components/admin/FilterBar/AttendeeTypeOptions"
import { DISCOUNT_KIND } from "components/admin/FilterBar/DiscountOptions"
import { FORM_KIND } from "components/admin/FilterBar/FormOptions"
import { QUESTION_KIND } from "components/admin/FilterBar/QuestionOptions"
import { SCHOLARSHIP_KIND } from "components/admin/FilterBar/ScholarshipOptions"
import { isPresent, cleanString } from "utils/helpers"

/**
 * @function
 * @name isArrayFormat
 * @desc Checks if the given type should be formatted as array or string
 *
 * @example
 *  // returns false
 *  isArrayFormat("nameOrEmail", "value")
 *
 *  // returns true
 *  isArrayFormat("question", "extra")
 */
const isArrayFormat = (uniqueKind, key) => {
  const kind = cleanString(uniqueKind, /[0-9]/g)

  if (
    kind === ADD_ON_KIND ||
    (kind === ASSIGNMENT_KIND && key === "extra") ||
    (kind === ATTENDANCE_KIND && key === "extra") ||
    (kind === ATTENDEE_TYPE_KIND && key === "value") ||
    (kind === DISCOUNT_KIND && key === "value") ||
    (kind === QUESTION_KIND && key === "extra") ||
    (kind === SCHOLARSHIP_KIND && key === "value") ||
    (kind === FORM_KIND && key === "value")
  ) {
    return true
  }

  return false
}

/**
 * @function
 * @name searchParamsToObject
 * @desc Splits URL search params into local search object
 *
 * @example
 *  const params = { attendeeType_value: "1" }
 *  // returns { attendeeType: { kind: "attendeeType", value: "1" } }
 *  searchParamsToObject(
 *    { attendeeType_value: "1" }
 * )
 */
const searchParamsToObject = searchParams => {
  return Object.entries(searchParams).reduce((obj, [k, v]) => {
    const [uniqueKind, key] = k.split("_")

    const kind = cleanString(uniqueKind, /[0-9]/g)

    return {
      ...obj,
      [uniqueKind]: {
        ...obj[uniqueKind],
        kind,
        [key]: isArrayFormat(kind, key) ? v.split(",") : v
      }
    }
  }, {})
}

/**
 * @function
 * @name objectToUrlParams
 * @desc Converts search object to URL param string
 *
 * @example
 *  // returns "&attendeeType_value=1"
 *  objectToUrlParams(
 *    { attendeeType: { kind: "attendeeType", value: "1" } }
 *    { show_canceled: true }
 * )
 */
const objectToUrlParams = (searchParams, params) => {
  const skipKeys = ["kind", "key", "label"]

  return isPresent(searchParams)
    ? `${isPresent(params) ? "&" : ""}${Object.entries(searchParams)
        .map(([kind, attrs]) =>
          Object.entries(attrs)
            .map(([key, value]) => (skipKeys.includes(key) ? null : `${kind}_${key}=${value}`))
            .filter(x => x !== null)
            .join("&")
        )
        .join("&")}`
    : ""
}

/**
 * @function
 * @name objectToSearchParams
 * @desc Converts search object to URL search[] param string
 *
 * @example
 *  // returns "&search[]={"kind":"attendeeType","value":"1"}"
 *  objectToSearchParams(
 *    { attendeeType: { kind: "attendeeType", value: "1" } }
 *    { show_canceled: true }
 * )
 */
const objectToSearchParams = (searchParams, params) => {
  return isPresent(searchParams)
    ? `${isPresent(params) ? "&" : ""}search[]=${Object.values(searchParams)
        .map(v => {
          const items = Object.assign({}, v)
          delete items.key
          delete items.label

          return JSON.stringify(items)
        })
        .join("&search[]=")}`
    : ""
}

/**
 * @function
 * @name splitUrlParams
 * @desc Separate base params from nested search params
 *
 * @example
 *  // returns { searches: { attendeeType: { kind: "attendeeType", value: "1" } }, params: { show_canceled: true } }
 *  splitUrlParams("show_canceled=true&attendeeType_value=1")
 */
export const splitUrlParams = urlParams => {
  const paramsFromUrl = Object.fromEntries(new URLSearchParams(urlParams))

  const {
    show_canceled,
    show_waitlist,
    show_attendance,
    attendance_report_id,
    page,
    reset,
    sort_by,
    sort_dir,
    ...searchParams
  } = paramsFromUrl

  return {
    searches: searchParamsToObject(searchParams),
    params: {
      ...(show_canceled && { show_canceled }),
      ...(show_waitlist && { show_waitlist }),
      ...(show_attendance && { show_attendance }),
      ...(attendance_report_id && { attendance_report_id }),
      ...(reset && { reset }),
      ...(page && { page }),
      ...(sort_by && { sort_by }),
      ...(sort_dir && { sort_dir })
    }
  }
}

/**
 * @function
 * @name createRegistrationPaths
 * @desc Creates URL path and JS Request path based on path and params
 *
 * @example
 *  // returns { requestPath: "", urlPath: "" }
 *  createRegistrationPaths("/", {}, {})
 */
export const createRegistrationPaths = (paths, params, searchParams) => {
  const requestPath = `${paths.request}${isPresent({ ...params, ...searchParams }) ? "?" : ""}`

  const urlPath = `${paths.url}${isPresent({ ...params, ...searchParams }) ? "?" : ""}`

  const paramsToString = isPresent(params) ? `${new URLSearchParams(params).toString()}` : ""

  return {
    requestPath: requestPath + paramsToString + objectToSearchParams(searchParams, params),
    urlPath: urlPath + paramsToString + objectToUrlParams(searchParams, params)
  }
}
